import { useEffect, useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { adminApi } from '../../api';
import type { AdminCredentialDto, CredentialCountsDto } from '../../api';
import { VerificationStatus } from '../../api/generated/boticaAPI.schemas';
import { Tabs, type TabItem } from '../../components/ui/Tab';
import { Table, type Column } from '../../components/ui/Table';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';

/* ─── Status helpers ─────────────────────────────────────────────────────── */

const STATUS_LABEL: Record<VerificationStatus, string> = {
  [VerificationStatus.PendingDocuments]: 'Unverified',
  [VerificationStatus.PendingReview]: 'Pending',
  [VerificationStatus.Verified]: 'Approved',
  [VerificationStatus.Rejected]: 'Rejected',
};

const STATUS_BADGE: Record<VerificationStatus, 'neutral' | 'pending' | 'approved' | 'rejected'> = {
  [VerificationStatus.PendingDocuments]: 'neutral',
  [VerificationStatus.PendingReview]: 'pending',
  [VerificationStatus.Verified]: 'approved',
  [VerificationStatus.Rejected]: 'rejected',
};

type TabValue = 'all' | 'pending' | 'approved' | 'rejected' | 'unverified';

const TAB_STATUS: Record<TabValue, VerificationStatus | undefined> = {
  all: undefined,
  pending: VerificationStatus.PendingReview,
  approved: VerificationStatus.Verified,
  rejected: VerificationStatus.Rejected,
  unverified: VerificationStatus.PendingDocuments,
};

const EMPTY_MESSAGES: Record<TabValue, { title: string; desc: string }> = {
  all: {
    title: 'No submissions yet',
    desc: 'Credentials submitted by pharmacists will appear here.',
  },
  pending: {
    title: 'No pending credentials',
    desc: 'All submissions have been reviewed.',
  },
  approved: {
    title: 'No approved credentials',
    desc: 'Approved credentials will appear here.',
  },
  rejected: {
    title: 'No rejected credentials',
    desc: 'Rejected credentials will appear here.',
  },
  unverified: {
    title: 'No unverified users',
    desc: 'All users have submitted their documents.',
  },
};

const PAGE_SIZE = 25;

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

/* ─── CredentialsPage ────────────────────────────────────────────────────── */

export function CredentialsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialTab = (searchParams.get('tab') as TabValue | null) ?? 'all';
  const [activeTab, setActiveTab] = useState<TabValue>(initialTab);
  const [sortDir, setSortDir] = useState<'desc' | 'asc'>('desc');
  const [page, setPage] = useState(1);

  const [rows, setRows] = useState<AdminCredentialDto[]>([]);
  const [counts, setCounts] = useState<CredentialCountsDto>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCredentials = () => {
    setLoading(true);
    setError(null);
    adminApi
      .getCredentials()
      .then((res) => {
        setRows(res.credentials ?? []);
        setCounts(res.counts ?? {});
      })
      .catch(() => setError('Failed to load list. Try again.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchCredentials();
  }, []);

  const handleTabChange = (tab: string) => {
    setActiveTab(tab as TabValue);
    setPage(1);
    setSearchParams({ tab }, { replace: true });
  };

  const filtered = useMemo(() => {
    const statusFilter = TAB_STATUS[activeTab];
    const base =
      statusFilter === undefined
        ? rows
        : rows.filter((r) => r.verificationStatus === statusFilter);

    return [...base].sort((a, b) => {
      const aDate = a.submittedAt ? new Date(a.submittedAt).getTime() : 0;
      const bDate = b.submittedAt ? new Date(b.submittedAt).getTime() : 0;
      return sortDir === 'desc' ? bDate - aDate : aDate - bDate;
    });
  }, [rows, activeTab, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const tabs: TabItem[] = [
    { value: 'all',        label: 'All',        count: counts.total },
    { value: 'pending',    label: 'Pending',    count: counts.pendingReview },
    { value: 'approved',   label: 'Approved',   count: counts.verified },
    { value: 'rejected',   label: 'Rejected',   count: counts.rejected },
    { value: 'unverified', label: 'Unverified', count: counts.pendingDocuments },
  ];

  const columns: Column<AdminCredentialDto>[] = [
    {
      key: 'name',
      header: 'Name',
      render: (r) => <span style={{ fontWeight: 500 }}>{r.userDisplayName ?? '—'}</span>,
    },
    {
      key: 'email',
      header: 'Email',
      render: (r) => <span style={{ color: 'var(--text-secondary)' }}>{r.userEmail ?? '—'}</span>,
    },
    {
      key: 'submitted',
      header: 'Submitted',
      render: (r) => (
        <span style={{ color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
          {formatDate(r.submittedAt)}
        </span>
      ),
    },
    {
      key: 'mpps',
      header: 'MPPS No.',
      render: (r) =>
        r.mppsNumber ? (
          <code
            style={{
              fontFamily: 'monospace',
              fontSize: 'var(--text-xs)',
              background: 'var(--warm-gray-100)',
              padding: '2px 6px',
              borderRadius: 'var(--radius-sm)',
            }}
          >
            {r.mppsNumber}
          </code>
        ) : (
          <span style={{ color: 'var(--text-placeholder)' }}>—</span>
        ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (r) => {
        const v = r.verificationStatus ?? VerificationStatus.PendingDocuments;
        return <Badge variant={STATUS_BADGE[v]}>{STATUS_LABEL[v]}</Badge>;
      },
    },
  ];

  const empty = EMPTY_MESSAGES[activeTab];

  return (
    <div style={{ maxWidth: 'var(--content-max-width)' }}>
      <Tabs value={activeTab} onChange={handleTabChange} items={tabs} />

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          margin: '16px 0 12px',
          minHeight: 32,
        }}
      >
        {error ? (
          <span style={{ fontSize: 'var(--text-sm)', color: 'var(--badge-rejected-text)' }}>
            {error}
          </span>
        ) : (
          <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-tertiary)' }}>
            {!loading && `${filtered.length} result${filtered.length !== 1 ? 's' : ''}`}
          </span>
        )}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {error && (
            <Button variant="secondary" size="sm" onClick={fetchCredentials}>
              Retry
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
              setPage(1);
            }}
          >
            Date {sortDir === 'desc' ? '↓' : '↑'}
          </Button>
        </div>
      </div>

      <Table
        columns={columns}
        rows={pageRows}
        getKey={(r) => r.id ?? ''}
        loading={loading}
        emptyMessage={empty.title}
        emptyDescription={empty.desc}
        onRowClick={(r) =>
          navigate(`/credentials/${r.id}`, { state: { credential: r } })
        }
      />

      {!loading && !error && totalPages > 1 && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 8,
            marginTop: 16,
          }}
        >
          <Button
            variant="ghost"
            size="sm"
            disabled={page === 1}
            onClick={() => setPage((p) => p - 1)}
          >
            Previous
          </Button>
          <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-tertiary)' }}>
            {page} / {totalPages}
          </span>
          <Button
            variant="ghost"
            size="sm"
            disabled={page === totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}
